import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Pencil } from "lucide-react";
import { searchAsStrings } from "@/components/portal/apps/reporting/custom/columnFilterParams";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";

import { SpaceView } from "@/components/portal/spaces/SpaceView";
import {
  encodeParamValuesForUrl,
  type SpaceParamValues,
} from "@/components/portal/spaces/spaceParams";
import { DecalToggleButton } from "@/components/portal/charts/DecalToggleButton";
import { spacesApi } from "@/services/api/spaces";

/**
 * Space view route. Search params are an open map of parameter-key → value
 * pairs — they seed the space's filter state on first load and rewrite back to
 * the URL when the user applies filters, making any space view shareable by
 * URL. Unknown keys are dropped by SpaceView. Numbers and booleans come back
 * as text (`searchAsStrings`), so a pasted link's `Year=2026` survives (#1411).
 */
type SpaceSearch = Record<string, string>;

export const Route = createFileRoute("/portal/spaces/$spaceId/")({
  component: SpacePage,
  validateSearch: (search: Record<string, unknown>): SpaceSearch => searchAsStrings(search),
});

function SpacePage() {
  const { spaceId } = useParams({ from: "/portal/spaces/$spaceId/" });
  const search = useSearch({ from: "/portal/spaces/$spaceId/" });
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";

  // Mirror filter changes into the URL so the address bar IS the shareable
  // link. `replace: true` avoids stuffing every Apply click into history.
  const handleParamValuesChange = (next: SpaceParamValues) => {
    navigate({
      to: "/portal/spaces/$spaceId",
      params: { spaceId },
      search: () => encodeParamValuesForUrl(next),
      replace: true,
    });
  };

  const space = useQuery({
    queryKey: ["space", orgId, spaceId],
    queryFn: () => spacesApi.getSpace(token!, orgId, spaceId),
    enabled: !!token && !!orgId && !!spaceId,
  });

  if (space.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (space.error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
        {space.error instanceof Error ? space.error.message : "Failed to load space"}
      </div>
    );
  }

  if (!space.data) return null;

  return (
    <>
      <Helmet>
        <title>{space.data.name}</title>
      </Helmet>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Button asChild type="button" variant="ghost" size="sm" className="-ml-2 gap-1">
            <Link to="/portal/spaces">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to spaces
            </Link>
          </Button>
          <div className="flex items-center gap-1">
            {/* Second home for the decal toggle (D2) — so a full-screen
                dashboard can flip pattern fills without leaving the view. */}
            <DecalToggleButton className="h-8 w-8 rounded-full text-muted-foreground hover:bg-accent hover:text-foreground" />
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <Link to="/portal/spaces/$spaceId/edit" params={{ spaceId }}>
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Link>
            </Button>
          </div>
        </div>
        <SpaceView
          // Keyed so a Space drill-through into another Space starts with that
          // Space's own seeded filters, not this one's held draft/applied state.
          key={spaceId}
          space={space.data}
          spaceId={spaceId}
          onOpenSpace={(spaceSlug, nextSearch) =>
            navigate({
              to: "/portal/spaces/$spaceId",
              params: { spaceId: spaceSlug },
              search: () => nextSearch,
            })
          }
          initialParamValues={search}
          onAppliedParamValuesChange={handleParamValuesChange}
        />
      </div>
    </>
  );
}
